import React, { useState, useRef, useEffect } from 'react';
// FIX: Use relative path to resolve types module.
import { View, Notification } from '../../types';
import { BellIcon, ClipboardDocumentListIcon, PlayIcon, CheckIcon, TrashIcon, QuestionMarkCircleIcon, DocumentTextIcon, BoltIcon, PuzzlePieceIcon } from './Icons';

interface NotificationBellProps {
    notifications: Notification[];
    onNavigate: (view: View, payload?: object) => void;
    onMarkAsRead: (id: string) => void;
    onMarkAllAsRead: () => void;
    onClearRead: () => void;
    /**
     * Neo panel theo cạnh nào của icon chuông. Mặc định 'right' (đúng khi
     * chuông nằm gần mép phải, vd header/topbar). Dùng 'left' khi chuông
     * nằm gần mép trái màn hình (vd hàng logo sidebar) — nếu không panel
     * rộng 320-384px sẽ tràn ra ngoài viewport bên trái, bị cắt chữ.
     */
    align?: 'left' | 'right';
}

const NotificationIcon: React.FC<{ icon: Notification['icon'] }> = ({ icon }) => {
    switch (icon) {
        case 'homework': return <ClipboardDocumentListIcon className="h-5 w-5 text-yellow-500" />;
        case 'task': return <ClipboardDocumentListIcon className="h-5 w-5 text-teal-500" />;
        case 'announcement': return <DocumentTextIcon className="h-5 w-5 text-sky-500" />;
        case 'video': return <PlayIcon className="h-5 w-5 text-purple-500" />;
        case 'quiz': return <QuestionMarkCircleIcon className="h-5 w-5 text-purple-500" />;
        case 'lesson': return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
        case 'game': return <BoltIcon className="h-5 w-5 text-orange-500" />;
        case 'activity': return <PuzzlePieceIcon className="h-5 w-5 text-indigo-500" />;
        case 'credit': return <BoltIcon className="h-5 w-5 text-amber-500" />;
        case 'info': return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
        default: return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
};

const timeAgo = (date: string): string => {
    const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " năm trước";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " tháng trước";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " ngày trước";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " giờ trước";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " phút trước";
    return "Vừa xong";
};

const NotificationBell: React.FC<NotificationBellProps> = ({ notifications, onNavigate, onMarkAsRead, onMarkAllAsRead, onClearRead, align = 'right' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const unreadCount = notifications.filter(n => !n.isRead).length;
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.isRead) {
            onMarkAsRead(notification.id);
        }
        if (notification.link?.view) {
            onNavigate(notification.link.view as View, notification.link.payload || {});
        }
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 rounded-full hover:bg-gray-100">
                <BellIcon className="h-6 w-6 text-gray-600" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white ping-blink-dot"></span>
                )}
            </button>
            {isOpen && (
                <div className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} mt-2 w-80 sm:w-96 max-w-[90vw] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20`}>
                    <div className="p-3 border-b flex justify-between items-center">
                        <h3 className="font-semibold text-gray-800">Thông báo</h3>
                        <div className="flex items-center gap-2">
                           <button onClick={onMarkAllAsRead} className="text-xs font-medium text-teal-600 hover:underline" disabled={unreadCount === 0}>Đánh dấu đã đọc</button>
                           <span className="text-gray-300">|</span>
                           <button onClick={onClearRead} className="text-xs font-medium text-teal-600 hover:underline">Xóa thông báo đã đọc</button>
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <p className="text-center text-sm text-gray-500 py-10">Bạn chưa có thông báo mới.</p>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNotificationClick(notification); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notification); } }}
                                    className={`flex items-start gap-3 p-3 border-b border-gray-100 transition-colors cursor-pointer hover:bg-gray-50`}
                                >
                                    {!notification.isRead && <div className="mt-1.5 w-2 h-2 rounded-full bg-teal-500 flex-shrink-0"></div>}
                                    <div className={`flex-shrink-0 ${notification.isRead ? 'ml-4' : ''}`}>
                                        <NotificationIcon icon={notification.icon} />
                                    </div>
                                    <div className="flex-grow">
                                        <p className="text-sm text-gray-700">{notification.message}</p>
                                        <p className="text-xs text-gray-500 mt-1">{timeAgo(notification.timestamp)}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;